import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { FileText, Plus, Search, Loader2, Trash2, ExternalLink, CheckCircle2, Send, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft:     { label: 'Bozza',    color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  sent:      { label: 'Inviata',  color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  paid:      { label: 'Pagata',   color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  overdue:   { label: 'Scaduta',  color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { label: 'Annullata', color: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
};

const PAYMENT_METHODS = [
  { value: 'bonifico', label: 'Bonifico bancario' },
  { value: 'contanti', label: 'Contanti' },
  { value: 'carta_credito', label: 'Carta di credito' },
  { value: 'assegno', label: 'Assegno' },
  { value: 'riba', label: 'RiBa' },
];

const formatEuro = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Invoices() {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showMarkPaid, setShowMarkPaid] = useState(null);
  const [payMethod, setPayMethod] = useState('bonifico');
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [invRes, statsRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/invoices/stats`),
      ]);
      setInvoices(invRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const markSent = async (id) => {
    try {
      await axios.patch(`${API}/invoices/${id}/send`);
      toast.success('Fattura segnata come inviata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const markPaid = async () => {
    if (!showMarkPaid) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/invoices/${showMarkPaid}/mark-paid`, { payment_method: payMethod });
      toast.success('Fattura segnata come pagata');
      setShowMarkPaid(null);
      fetchAll();
    } catch { toast.error('Errore'); } finally { setSaving(false); }
  };

  const deleteInvoice = async (id, num) => {
    if (!window.confirm(`Eliminare fattura ${num}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Fattura eliminata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = !search || [inv.invoice_number, inv.company_name, inv.assigned_name]
      .filter(Boolean).some(f => f.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = !filterStatus || inv.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>Fatture</h1>
          <p className="text-muted-foreground text-sm mt-1">{invoices.length} fatture totali</p>
        </div>
        <Button onClick={() => navigate('/orders')} variant="outline" data-testid="go-orders-btn">
          Crea da Ordine
        </Button>
      </div>

      {/* Financial stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Totale Fatturato', value: formatEuro(stats.total_invoiced), icon: FileText, color: 'text-primary' },
            { label: 'Incassato', value: formatEuro(stats.total_paid), icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
            { label: 'Da Incassare', value: formatEuro(stats.total_outstanding), icon: Send, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Scadute', value: formatEuro(stats.total_overdue), icon: AlertCircle, color: 'text-red-600 dark:text-red-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className={color} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-base font-bold mt-0.5" style={{fontFamily:'Manrope, sans-serif'}}>{value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Status pills */}
      <div className="flex gap-2 flex-wrap">
        {[null, 'draft', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setFilterStatus(s || '')}
            data-testid={`filter-${s || 'all'}`}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              filterStatus === (s || '') ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
            }`}
          >
            {s ? `${STATUS_CONFIG[s]?.label} (${stats?.by_status?.[s] || 0})` : `Tutte (${invoices.length})`}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Cerca per numero, cliente..." className="pl-9 h-9" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">Nessuna fattura</h3>
          <p className="text-muted-foreground text-sm">Crea un ordine e genera la fattura dall'ordine</p>
          <Button className="mt-4" onClick={() => navigate('/orders')}><Plus size={14} className="mr-2" />Vai agli Ordini</Button>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">N° Fattura</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Commerciale</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Emissione</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Scadenza</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Totale</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Stato</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
                </tr>
              </thead>
              <tbody className="stagger-children">
                {filtered.map(inv => {
                  const id = inv._id || inv.id;
                  const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                  const isOverdue = inv.status === 'overdue';
                  return (
                    <tr key={id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-primary text-sm">{inv.invoice_number}</span>
                        {inv.order_number && <p className="text-xs text-muted-foreground">{inv.order_number}</p>}
                      </td>
                      <td className="px-4 py-3 font-medium">{inv.company_name || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{inv.assigned_name || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className={`px-4 py-3 hidden lg:table-cell ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatEuro(inv.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                        {inv.paid_date && <p className="text-xs text-muted-foreground mt-0.5">Pagata: {new Date(inv.paid_date).toLocaleDateString('it-IT')}</p>}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {canEdit('invoices') && inv.status === 'draft' && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markSent(id)} data-testid={`mark-sent-${id}`}>
                              <Send size={11} className="mr-1" />Invia
                            </Button>
                          )}
                          {canEdit('invoices') && (inv.status === 'sent' || inv.status === 'overdue') && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-green-600 hover:text-green-700" onClick={() => setShowMarkPaid(id)} data-testid={`mark-paid-${id}`}>
                              <CheckCircle2 size={11} className="mr-1" />Pagata
                            </Button>
                          )}
                          {canDelete('invoices') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteInvoice(id, inv.invoice_number)}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mark Paid Dialog */}
      <Dialog open={!!showMarkPaid} onOpenChange={() => setShowMarkPaid(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registra Pagamento</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Metodo di pagamento</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowMarkPaid(null)}>Annulla</Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={markPaid} disabled={saving} data-testid="confirm-paid-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <CheckCircle2 size={14} className="mr-2" />Conferma Pagamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
